import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, MapPin, Trophy, Users, Handshake, Target, Compass, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_8daf730c-66f1-48f9-9ec4-f1965e82cf39/artifacts/biwwrc3n_Image%208.JPG";
const TEAM_IMAGE = "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?q=85&w=800&auto=format&fit=crop";

const values = [
  {
    icon: Trophy,
    title: "Excellence",
    description: "Nous visons la perfection dans chaque détail de nos réalisations.",
  },
  {
    icon: Handshake,
    title: "Confiance",
    description: "Une relation transparente et honnête avec nos clients depuis 2007.",
  },
  {
    icon: Target,
    title: "Précision",
    description: "Des finitions soignées qui témoignent de notre savoir-faire artisanal.",
  },
  {
    icon: Clock,
    title: "Ponctualité",
    description: "Respect des délais convenus pour chaque projet, sans compromis.",
  },
];

const milestones = [
  { year: "2002", title: "Création de Qualidéco", description: "Fondation de l'entreprise à Mâcon" },
  { year: "2007", title: "Certification Qualibat", description: "Obtention de la certification professionnelle" },
  { year: "2012", title: "Certification RGE", description: "Reconnaissance Garant de l'Environnement" },
  { year: "2017", title: "Expansion régionale", description: "Extension de notre zone d'intervention" },
  { year: "2027", title: "25 ans d'expertise", description: "Plus de 1000 projets réalisés" },
];

export default function AboutPage() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await axios.get(`${API}/company`);
        setCompany(response.data);
      } catch (error) {
        console.error("Error fetching company info:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-[#F8F9FA] py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Notre Histoire
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#333333] mt-3">
              À Propos de Qualidéco
            </h1>
            <p className="text-[#64748B] text-lg mt-6">
              Depuis 2002, nous mettons notre passion et notre savoir-faire au service de vos projets 
              de rénovation et décoration en Saône-et-Loire.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-sm overflow-hidden shadow-2xl">
                <img
                  src={ABOUT_IMAGE}
                  alt="L'équipe Qualidéco au travail"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 bg-[#E74C3C] text-white p-8 rounded-sm shadow-xl">
                <p className="text-5xl font-bold font-['Barlow_Condensed']">25+</p>
                <p className="text-sm opacity-90">Années d'expérience</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-[#333333]">
                Une Entreprise Familiale au Service de Vos Projets
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                {company?.description || "Fondée en 2002, Qualidéco est une entreprise spécialisée dans les travaux de finition et de décoration intérieure et extérieure. Avec plus de 25 ans d'expérience, nous accompagnons bailleurs sociaux, particuliers, architectes et collectivités dans tous leurs projets de rénovation et d'embellissement."}
              </p>
              <p className="text-[#64748B] leading-relaxed">
                Notre équipe de professionnels qualifiés met tout en œuvre pour garantir des prestations 
                de qualité, dans le respect des délais et de votre budget. Chaque projet est unique, 
                c'est pourquoi nous prenons le temps d'écouter vos besoins pour vous proposer des 
                solutions adaptées.
              </p>
              
              {/* Certifications */}
              <div className="bg-[#F8F9FA] p-6 rounded-sm">
                <h4 className="font-bold text-[#333333] mb-4">Nos Certifications</h4>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-sm shadow-sm">
                    <span className="bg-blue-600 text-white text-sm font-bold px-4 py-2 rounded-full">
                      QUALIBAT
                    </span>
                  </div>
                  <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-sm shadow-sm">
                    <span className="bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full">
                      RGE
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#F8F9FA]" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Nos Engagements
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#333333] mt-3">
              Les Valeurs qui Nous Animent
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-sm shadow-sm hover:shadow-lg transition-shadow text-center"
                  data-testid={`value-${index}`}
                >
                  <div className="w-16 h-16 bg-[#1B93A4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon size={32} weight="duotone" className="text-[#1B93A4]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#333333] mb-3">{value.title}</h3>
                  <p className="text-[#64748B] text-sm">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24" data-testid="about-timeline">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#1B93A4] font-medium uppercase tracking-wider text-sm">
              Notre Parcours
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#333333] mt-3">
              Les Étapes Clés
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#E2E8F0] transform md:-translate-x-1/2" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex items-center gap-8 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  data-testid={`milestone-${index}`}
                >
                  {/* Dot */}
                  <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-[#E74C3C] rounded-full transform -translate-x-1/2 z-10" />
                  
                  {/* Content */}
                  <div className={`ml-20 md:ml-0 md:w-1/2 ${index % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16"}`}>
                    <span className="text-[#E74C3C] font-bold text-2xl font-['Barlow_Condensed']">
                      {milestone.year}
                    </span>
                    <h3 className="text-xl font-bold text-[#333333] mt-1">{milestone.title}</h3>
                    <p className="text-[#64748B] mt-2">{milestone.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-24 bg-[#333333]" data-testid="about-area">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Zone d'Intervention
              </h2>
              <p className="text-gray-300 leading-relaxed">
                Basés à Mâcon, nous intervenons dans toute la Saône-et-Loire et les départements 
                limitrophes. Notre équipe se déplace pour étudier votre projet et vous proposer 
                un devis adapté.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {(company?.service_area || ["Mâcon", "Saône-et-Loire", "Ain", "Rhône"]).map((area) => (
                  <div key={area} className="flex items-center gap-3 bg-white/5 p-4 rounded-sm">
                    <MapPin size={24} className="text-[#1B93A4]" />
                    <span className="text-white font-medium">{area}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <a
                  href="https://maps.google.com/?q=306+rue+Ampère+71000+Mâcon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#1B93A4] hover:text-white transition-colors font-medium"
                  data-testid="maps-link"
                >
                  <Compass size={20} />
                  Voir sur Google Maps
                  <ArrowRight size={16} />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-white/5 border border-white/10 p-8 rounded-sm">
                <h3 className="text-xl font-bold text-white mb-6">Notre Adresse</h3>
                <div className="space-y-4 text-gray-300">
                  <p className="flex items-start gap-3">
                    <MapPin size={24} className="text-[#1B93A4] flex-shrink-0" />
                    <span>
                      306 rue Ampère<br />
                      71000 Mâcon<br />
                      France
                    </span>
                  </p>
                </div>
                
                <div className="mt-8 pt-8 border-t border-white/10">
                  <h4 className="text-white font-medium mb-4">Horaires d'ouverture</h4>
                  <div className="space-y-2 text-gray-300 text-sm">
                    <p className="flex justify-between">
                      <span>Lundi - Vendredi</span>
                      <span>8h30-12h / 13h30-17h</span>
                    </p>
                    <p className="flex justify-between">
                      <span>Samedi - Dimanche</span>
                      <span>Fermé</span>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#F8F9FA]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#333333] mb-6">
              Prêt à Nous Faire Confiance ?
            </h2>
            <p className="text-[#64748B] text-lg mb-8">
              Contactez-nous pour discuter de votre projet. Notre équipe vous accompagne 
              à chaque étape pour concrétiser vos envies.
            </p>
            <Link to="/contact">
              <Button
                className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-10 py-6 rounded-sm text-base"
                data-testid="about-final-cta"
              >
                Contactez-nous
                <ArrowRight className="ml-2" size={20} weight="bold" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
